import { type Dispatch, type RefObject, type SetStateAction, useCallback } from 'react';
import { applySummaryCompletion, applySummaryStatus, createSummaryMetadata, DEFAULT_SUMMARY_STREAM_METADATA, type SummaryStreamMetadata } from '@/components/summarizer/summary-metadata';
import { streamTweetInsights } from '../async/fetchTweetInsights';
import type { StreamState } from '../types';
export interface StartTweetStreamOptions {
  abortRef: RefObject<AbortController | null>;
  endpoint: string;
  errorMessage: string;
  initialMetadata?: Partial<SummaryStreamMetadata>;
  requestBody: Record<string, unknown>;
  setState: Dispatch<SetStateAction<StreamState>>;
}
export function useTweetStreaming(isMountedReference: RefObject<boolean>) {
  return useCallback(async ({
    endpoint,
    requestBody,
    abortRef,
    setState,
    errorMessage,
    initialMetadata
  }: StartTweetStreamOptions) => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    // Start each stream with a fresh metadata snapshot so downstream consumers see
    // deterministic defaults even if the SSE payload trickles in slowly.
    let metadata = createSummaryMetadata({
      ...DEFAULT_SUMMARY_STREAM_METADATA,
      ...initialMetadata
    });
    const setStateSafe = (updater: SetStateAction<StreamState>) => {
      if (isMountedReference.current) {
        setState(updater);
      }
    };
    setStateSafe({
      status: 'loading',
      text: '',
      error: null,
      metadata
    });
    let aggregated = '';
    try {
      await streamTweetInsights({
        endpoint,
        requestBody,
        signal: controller.signal,
        errorMessage,
        callbacks: {
          onStatus: payload => {
            metadata = applySummaryStatus(metadata, payload);
            setStateSafe(previous_0 => ({
              ...previous_0,
              metadata
            }));
          },
          onToken: ({
            aggregated: next
          }) => {
            aggregated = next;
            setStateSafe({
              status: 'streaming',
              text: aggregated,
              error: null,
              metadata
            });
          },
          onDone: (payload_0, finalText) => {
            aggregated = finalText;
            metadata = applySummaryCompletion(metadata, payload_0);
            setStateSafe({
              status: 'complete',
              text: finalText,
              error: null,
              metadata
            });
          },
          onComplete: finalText_0 => {
            aggregated = finalText_0;
            setStateSafe(previous_1 => ({
              ...previous_1,
              text: finalText_0
            }));
          },
          onError: (message_0, currentAggregated) => {
            aggregated = currentAggregated;
            setStateSafe({
              status: 'error',
              text: currentAggregated,
              error: message_0,
              metadata
            });
            controller.abort();
          }
        }
      });
      if (!aggregated) {
        setStateSafe({
          status: 'complete',
          text: '',
          error: null,
          metadata
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError' || error instanceof Error && error.name === 'AbortError') {
        return;
      }
      const message = error instanceof Error ? error.message : errorMessage;
      setStateSafe(previous => ({
        status: 'error',
        text: previous.text,
        error: message,
        metadata
      }));
    } finally {
      abortRef.current = null;
    }
  }, [isMountedReference]);
}